// library imports
import { useState, useEffect, useContext } from "react";
import { Empty, Skeleton, Flex } from "antd";
import dayjs from "dayjs";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// component imports
import CustomLineChart from "../common/CustomLineChart";
// config imports
import { MQTT_REFRESH_RATE } from "../../configs/configs";

export default function SocChartContent({
  parentContainerHeight,
  powerEnergyDateSelection,
  powerEnergyDateTypeSelection,
}) {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  const [loading, setLoading] = useState(true);

  const [socChartData, setSocChartData] = useState([]);
  const [chartDate, setChartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [maxSOC, setMaxSOC] = useState("-");
  const [minSOC, setMinSOC] = useState("-");

  /** API to get SOC chart data*/
  function getSocChartData() {
    fetchWrapper({
      endpoint_url: "dashboard/getSocChartData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_selected: powerEnergyDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        power_energy_chart_date_type: powerEnergyDateTypeSelection,
      },

      onSuccess: (response) => {
        setSocChartData(response.data.soc_chart_data);
        setChartDate(response.data.chart_date);
        setMaxSOC(response.data.max_soc);
        setMinSOC(response.data.min_soc);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get SOC chart data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    setLoading(true);
    if (siteSelected && powerEnergyDateSelection) {
      getSocChartData();

      const timer = setInterval(() => {
        getSocChartData();
      }, MQTT_REFRESH_RATE * 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [siteSelected, powerEnergyDateSelection, powerEnergyDateTypeSelection]);

  return (
    <div className="h-100 w-100">
      <div
        style={{
          fontWeight: "bold",
          fontSize: "1rem",
          paddingLeft: "12px",
        }}
      >
        State of Charge {loading ? "" : `(${chartDate})`}
      </div>
      {loading === false ? (
        socChartData.length !== 0 ? (
          <div>
            <div
              style={{
                paddingLeft: "12px",
                paddingTop: "8px",
              }}
            >
              <span>Highest:&nbsp;</span>
              <span style={{ fontWeight: "bold" }}>
                {maxSOC !== "-" ? maxSOC.toFixed(1) + "%" : maxSOC}
              </span>
              <span>&nbsp;; Lowest:&nbsp;</span>
              <span style={{ fontWeight: "bold" }}>
                {minSOC !== "-" ? minSOC.toFixed(1) + "%" : minSOC}
              </span>
            </div>
            <CustomLineChart
              scale={{ domain: [0, 100] }}
              data_line={socChartData.map((item) => {
                const newItem = { ...item };
                for (const key in newItem) {
                  if (newItem[key] === "-") {
                    newItem[key] = null;
                  }
                }
                return newItem;
              })}
              height={parentContainerHeight - 24 - 26 - 30} //parent container height - card body padding - title height - statistic data height
            />
          </div>
        ) : (
          <Flex
            vertical
            justify="center"
            align="center"
            style={{
              height: `calc(${parentContainerHeight}px - 24px - 26px)`,
            }}
          >
            <Empty />
          </Flex>
        )
      ) : (
        <Skeleton
          active={true}
          style={{ padding: "1rem", maxWidth: "70%", margin: "auto" }}
          paragraph={{ rows: 5 }}
        />
      )}
    </div>
  );
}
